import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Compbaser } from "../../../../components/compbaser";
import { AppService, ContainerService, ImportService, LocalStoreService, SceneAddService, StoreSelectorsService } from "../../../../services";
import {
    actionAddSceneItem,
    actionCRUDRecord,
    actionRemoveSceneBlocks,
    actionRemoveScenes,
    actionSceneJumpLinked,
    actionSelectedSceneId,
    actionSelectedSceneItemId,
    actionSelectedSceneTouchId,
    actionSetEditorMode,
    actionUpdateSceneItem,
} from "../../../../store/actions/user.actions";
import { ExperienceLevelType, IAccessTable, IAppSettingsWrap, ICRUDCommand, ICRUDRecord, Id, IPlayerProps, ISceneBlock, ISceneTreeSliceResults } from "../../../../interfaces";
import { combineLatest, filter, map, take } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { TranslocoService } from "@ngneat/transloco";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { MatDialog } from "@angular/material/dialog";
import { DialogPopComponent } from "../../../../components/dialog-pop/dialog-pop.component";
import { DialogPreviewComponent, DialogPreviewData } from "../../../../components/dialog-preview/dialog-preview.component";
import { StationAddPlayerComponent } from "../../../stations/station-add-player/station-add-player.component";
import { NavigationButtonSize, NavigationButtonType, Slide, TouchLib } from "../touchLib";
import { ISliderBlock, SliderTextBlocks, SliderImageBlocks, SliderVideoBlocks } from "../touch-tools-constants";
import { DialogTouchActionComponent } from "../touch-action-dialog/touch-action-dialog.component";
import { animate, state, style, transition, trigger } from "@angular/animations";
import { MatDrawer, MatDrawerContainer } from "@angular/material/sidenav";
import {
    faAnchor,
    faArrowUpRightDots,
    faAsterisk,
    faBell,
    faCalendarCheck,
    faCode,
    faCube,
    faHdd,
    faImage,
    faLayerGroup,
    faLocationCrosshairs,
    faObjectUngroup,
    faPhotoFilm,
    faPlugCircleCheck,
    faPuzzlePiece,
    faRandom,
    faReceipt,
    faWrench,
    IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { getFontIcons } from "../../../../common/get-icons";
import { BlockLabels, IAdvancedEvents, IBlockLabelProp } from "../../scene-blocks/block-constants";
import { compareIds, isMimeTypeExists, shortenString } from "../../../../common";
import * as _ from "lodash";

@Component({
    selector: "app-slideer-editor",
    templateUrl: "./touch-editor.component.html",
    styleUrls: ["./touch-editor.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger("fadeProperty", [
            state("void, hidden", style({ opacity: 0 })),
            state("visible", style({ opacity: 1 })),
            transition("* => visible", animate("1500ms cubic-bezier(0.0, 0.0, 0.2, 1)")),
            transition("* => hidden", animate("77ms cubic-bezier(0.4, 0.0, 1, 1)")),
        ]),
        trigger("fadeOutLabel", [state("visible", style({ opacity: 1 })), state("hidden", style({ opacity: 0 })), transition("visible => hidden", animate("800ms ease-out"))]),
    ],
})
export class TouchEditorComponent extends Compbaser implements OnInit, OnDestroy {
    @ViewChild("detailsTemplate", { static: true }) detailsTemplate!: TemplateRef<any>;
    @ViewChild("animationTemplate", { static: true }) animationTemplate!: TemplateRef<any>;
    @ViewChild("effectsTemplate", { static: true }) effectsTemplate!: TemplateRef<any>;
    @ViewChild("layersTemplate") layersTemplate!: TemplateRef<any>;
    @ViewChild("componentsTemplate") componentsTemplate!: TemplateRef<any>;
    @ViewChild("scenesTemplate") scenesTemplate!: TemplateRef<any>;
    @ViewChild("resourcesTemplate") resourcesTemplate!: TemplateRef<any>;
    @ViewChild("stickersTemplate") stickersTemplate!: TemplateRef<any>;
    @ViewChild("backgroundsTemplate") backgroundsTemplate!: TemplateRef<any>;
    @ViewChild("addAnimationTemplate") addAnimationTemplate!: TemplateRef<any>;

    @ViewChild("sidenav") sidenav: MatDrawer;
    @ViewChild(MatDrawerContainer) drawerContainer!: MatDrawerContainer;

    readonly faWrench = faWrench;
    readonly faAnchor = faAnchor;
    readonly faPlugCircleCheck = faPlugCircleCheck;
    readonly faRandom = faRandom;
    readonly faObjectUngroup = faObjectUngroup;
    readonly faHdd = faHdd;
    readonly faCode = faCode;
    readonly faReceipt = faReceipt;
    readonly faBell = faBell;
    readonly faCube = faCube;
    readonly faLayerGroup = faLayerGroup;
    readonly faPuzzlePiece = faPuzzlePiece;
    readonly faLocationCrosshairs = faLocationCrosshairs;
    readonly faPhotoFilm = faPhotoFilm;
    readonly faImage = faImage;
    readonly faAsterisk = faAsterisk;
    readonly faArrowUpRightDots = faArrowUpRightDots;
    readonly faCalendarCheck = faCalendarCheck;

    minimized = false;
    activeTemplate = "details";
    panelOpenState = true;
    visibility = "visible";
    sidenavOpened = true;
    sidenavMode: "side" | "over" = "side";
    multiple = false;

    viewMode: "list" | "grid" = "grid";
    showSettingsPanel = false;
    baseLayerLabelVisible = "visible";
    activeThumbSceneId: string = null;
    editingSlideSceneId: string = null;
    selectedSlideIndex: number = null;
    popupMenuId: number = null;
    updated = false;
    debouncedUpdated = false;
    private updateDebounceTimer: any;
    private currentSceneDataSubscription: any;
    transitioningSlideId: string = null;
    currentTheme = "light";

    sliderScene: IPlayerProps;
    slideList: Slide[] = [];
    width = "";
    height = "";
    sliderSceneId = "";
    sliderName = "";
    loading = false;
    mainComponent: "Collection" | "Playlist";

    // Tools panel state
    showToolsPanel = false;

    // Sidebar panel state
    sidebarMinimized = false;

    // Store properties
    isFullFeatures: boolean;
    coreAppData: IAppSettingsWrap;
    accessConfig: IAccessTable;
    experienceLevel: ExperienceLevelType;
    readonly experienceLevelType = ExperienceLevelType; // Expose enum to template
    dashboardMode: boolean;

    // Properties panel properties
    selectedSceneItemId: Id = "";
    sceneSlice: ISceneTreeSliceResults;
    blockName = "";
    blockIcon: IconDefinition;
    blockMimeName = "";
    blockVideoLinkName = "";
    isBlockAnimation = false;
    isNotNested = true;
    showEffects = true;
    isDataProvider = false;
    showEvents = true;
    isBlockAdvEvents: IAdvancedEvents = null;
    showPop: boolean = null;
    isSceneButNotNested = false;
    scriptingMode = false;
    isMimeMainPlayer = false;
    isRemoved = false;

    blockCategories = [];

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private toastr: ToastrService,
        private translocoService: TranslocoService,
        private localStoreService: LocalStoreService,
        private storeSelector: StoreSelectorsService,
        private importService: ImportService,
        private appService: AppService,
        private sceneAddService: SceneAddService,
        private containerService: ContainerService,
        public override dialog: MatDialog,
    ) {
        super();
        this.me = "touch-editor.component";
    }

    @HostListener("document:click", ["$event"])
    onDocumentClick(event: Event) {
        // Close menu when clicking outside
        if (this.popupMenuId !== null) {
            const target = event.target as HTMLElement;
            if (!target.closest(".menu-container")) {
                this.popupMenuId = null;
            }
        }
        if (this.showToolsPanel) {
            const target = event.target as HTMLElement;
            if (!target.closest(".tools-panel") && !target.closest(".floating-toolbar")) {
                this.closeToolsPanel();
            }
        }
    }

    @HostListener("wheel", ["$event"])
    onMouseWheel(event: WheelEvent) {
        const target = event.target as HTMLElement;
        if (target.closest(".slides-container")) {
            // Debounce wheel events to prevent rapid updates
            clearTimeout(this.wheelTimeout);
            this.wheelTimeout = setTimeout(() => {
                this.updateActiveSlideOnScroll();
            }, 100);
        }
    }

    private wheelTimeout: any;

    private debouncedUpdateGridView() {
        // Clear existing timer
        if (this.updateDebounceTimer) {
            clearTimeout(this.updateDebounceTimer);
        }

        // Set new timer for 3 seconds
        this.updateDebounceTimer = setTimeout(() => {
            this.debouncedUpdated = !this.debouncedUpdated;
            this.cdr.detectChanges();
        }, 555);
    }

    ngOnInit() {
        // Set editor mode to touch-editor
        this.storeSelector.dispatch(actionSetEditorMode({ editorMode: "touch-editor" }));

        // Initialize block categories with translations
        this.blockCategories = [
            { title: this.translocoService.translate("ai.texts", {}, this.translocoService.getActiveLang()), blocks: SliderTextBlocks },
            { title: this.translocoService.translate("ai.images", {}, this.translocoService.getActiveLang()), blocks: SliderImageBlocks },
            { title: this.translocoService.translate("ai.videos", {}, this.translocoService.getActiveLang()), blocks: SliderVideoBlocks },
        ];

        // Subscribe to theme changes
        this.cod(
            this.localStoreService.theme.subscribe((val) => {
                this.currentTheme = this.localStoreService.getStorageValConfig("theme");
                this.cdr.detectChanges(); // Trigger change detection when theme changes
            }),
        );

        // Load sidebar state from localStorage
        const savedSidebarState = this.localStoreService.getStorageValConfig("slideerSidebarMinimized");
        this.sidebarMinimized = savedSidebarState === "1";

        // Subscribe to store selectors for preview functionality
        this.cod(
            this.storeSelector.isFullFeatures().subscribe((v) => {
                this.isFullFeatures = v;
            }),
        );
        this.cod(this.storeSelector.getCoreData().subscribe((i_coreData: IAppSettingsWrap) => (this.coreAppData = i_coreData)));

        this.cod(
            this.storeSelector.getLocalStoreUpdates("ILocalStorageConfig").subscribe((e) => {
                this.experienceLevel = this.localStoreService.getStorageValConfig("experienceLevel");
                this.cdr.markForCheck();
            }),
        );

        // Use route params to set initial slider selection, then listen to getSliderSelection() for updates
        this.cod(
            combineLatest([this.route.paramMap, this.storeSelector.getTouchScenes()])
                .pipe(
                    map(([routeParams, i_scenes]) => {
                        return i_scenes.find((i_scene: IPlayerProps) => i_scene.Player.$.id === routeParams.get("id"));
                    }),
                    filter((scene) => !!scene),
                )
                .subscribe((i_scene: IPlayerProps) => {
                    if (!i_scene) return;
                    this.sliderScene = i_scene;
                    this.mainComponent = TouchLib.getComponentName(this.sliderScene);
                    this.isMimeMainPlayer = this.storeSelector.isMainSceneOrPlayer(i_scene.Player.$.mimeType);
                    this.processSlider();

                    // Now listen to getSliderSelection() for real-time updates
                    this.subscribeToSliderSelection();
                }),
        );

        this.cod(
            this.storeSelector.listenSelectedSceneItemId().subscribe((value) => {
                this.selectedSceneItemId = value;
                this.visibility = "hidden";
            }),
        );

        this.cod(
            this.storeSelector.listenPlayerDataOfSelectedSceneItemId().subscribe((i_sceneSlice: ISceneTreeSliceResults) => {
                if (!i_sceneSlice) return;
                this.sceneSlice = i_sceneSlice;
                this.blockName = this.sceneSlice.sceneItem.$.label;
                if (compareIds(this.sceneSlice.sceneItem.$.player, BlockLabels.BLOCKCODE_SCENE.id)) {
                    this.blockName = "scene";
                } else {
                    this.blockName = !!this.blockName ? this.blockName.toString() : "";
                    this.blockName = shortenString(this.blockName.toLocaleLowerCase(), 30);
                }
                this.showEffects = !(compareIds(this.sceneSlice.sceneItem.$.player, BlockLabels.MUSICLIST.id) || compareIds(this.sceneSlice.sceneItem.$.player, BlockLabels.MUSIC.id));
                const sceneBlock = this.storeSelector.getBlockLabelById(this.sceneSlice.sceneItem.$.player);
                this.showEvents = false;
                if (!sceneBlock) return;
                this.showEvents = sceneBlock.advEvents.send === true || sceneBlock.advEvents.receive === true || sceneBlock.advEvents.filter === true || sceneBlock.advEvents.source === true;
                const playerId = this.storeSelector.isBlockIdJsonAliased(this.sceneSlice.sceneItem);
                const blockLabelProps: IBlockLabelProp = this.storeSelector.getBlockLabelById(playerId);
                this.isDataProvider = blockLabelProps.dataProviderBlock && blockLabelProps.dataProviderBlock === true;
                this.isSceneButNotNested = this.blockName === "scene" && !this.sceneSlice.sceneItem.$.src;
                this.isNotNested = this.blockName === "scene" ? this.isSceneButNotNested : true;
                this.isBlockAnimation = sceneBlock.animation;
                this.isBlockAdvEvents = sceneBlock.advEvents;
                this.showPop = sceneBlock.pop;
                this.assignIcon(playerId, sceneBlock.types, sceneBlock.jsonItemBased);

                // Trigger immediate canvas update for keyboard movements and other scene item changes
                this.updated = !this.updated;
                this.cdr.detectChanges();
            }),
        );
    }

    assignIcon(i_blockPlayerId, sceneItemMimeType, isBlockItemBased) {
        const sceneMime = this.sliderScene.Player.$.mimeType;
        if (sceneItemMimeType && isBlockItemBased && sceneItemMimeType?.length > 0 && isMimeTypeExists(sceneItemMimeType, sceneMime)) {
            this.blockIcon = getFontIcons({ $: { id: sceneMime } }).icon;
        } else {
            this.blockIcon = getFontIcons({ $: { id: i_blockPlayerId } }).icon;
        }
        this.blockMimeName = getFontIcons({ $: { id: i_blockPlayerId } }).name;
        this.blockVideoLinkName = this.storeSelector.getBlockLabelById(this.sceneSlice.sceneItem.$.player).name;
    }

    animationDone(e) {
        if (e.toState === "hidden") {
            this.visibility = "visible";
        }
    }

    onMinimized() {
        this.minimized = true;
        setTimeout(() => {
            this.drawerContainer.updateContentMargins();
        });
    }

    onMaximized() {
        this.minimized = false;
        setTimeout(() => {
            this.drawerContainer.updateContentMargins();
        });
    }

    setActiveTemplate(templateName: string): void {
        if (this.activeTemplate != templateName) {
            this.onMaximized();
        } else {
            if (this.minimized) {
                this.onMaximized();
            } else {
                this.onMinimized();
            }
        }
        this.activeTemplate = templateName;
    }

    getTemplate(name: string): TemplateRef<any> | null {
        switch (name) {
            case "details":
                return this.detailsTemplate;
            case "animation":
                return this.animationTemplate;
            case "effects":
                return this.effectsTemplate;
            case "layers":
                return this.layersTemplate;
            case "components":
                return this.componentsTemplate;
            case "scenes":
                return this.scenesTemplate;
            case "resources":
                return this.resourcesTemplate;
            case "stickers":
                return this.stickersTemplate;
            case "backgrounds":
                return this.backgroundsTemplate;
            case "addAnimation":
                return this.addAnimationTemplate;
            default:
                return null;
        }
    }

    private subscribeToSliderSelection() {
        this.cod(
            this.storeSelector.getSliderSelection().subscribe((sliderSelection) => {
                // Only process if this matches the current slider being edited
                if (sliderSelection.sceneId === this.sliderSceneId && this.editingSlideSceneId !== sliderSelection.ScenePlaylistId) {
                    const index = this.slideList.findIndex((slide) => slide.sceneId === sliderSelection.ScenePlaylistId);
                    if (index !== -1) {
                        this.onSlideThumbClick(index, sliderSelection.ScenePlaylistId);
                        this.cdr.detectChanges();
                    }
                }
            }),
        );
    }

    private processSlider() {
        if (!this.sliderScene) return;
        const basicInfo = TouchLib.getSliderBasicInfo(this.sliderScene);
        this.width = basicInfo.width;
        this.height = basicInfo.height;
        this.sliderSceneId = basicInfo.sceneId;
        this.sliderName = basicInfo.name;

        const newSlideList = TouchLib.extractSlidesFromSlider(this.sliderScene);

        // const nextSelection = this.determineNextSelection(newSlideList);

        const isSame = this.slideList.length === newSlideList.length && this.slideList.every((slide, idx) => slide.sceneId === newSlideList[idx].sceneId);

        if (!isSame) {
            this.slideList = newSlideList;
        } else {
            // Update the main canvas immediately for responsive selection handles
            this.updated = !this.updated;
            // Use debounced update for grid view thumbnails only to prevent excessive re-renders
            this.debouncedUpdateGridView();
        }

        this.cdr.detectChanges();
        return;
    }

    onSlideChange(activeIndex: number) {
        // Handle slide change if needed
    }

    onSlideThumbClick(index: number, slideSceneId: string) {
        // Switch back to slides view when clicking any slide thumbnail
        this.showSettingsPanel = false;
        // Use the same masking effect for sidebar clicks
        this.onSlideClickWithMasking(index, slideSceneId, true);
    }

    private async onSlideClickWithMasking(index: number, slideSceneId: string, fromSidebar: boolean = false) {
        this.selectedSlideIndex = index;
        this.activeThumbSceneId = slideSceneId;

        // Only proceed if this slide is not currently being edited
        if (this.editingSlideSceneId === slideSceneId) {
            return;
        }

        // Step 1: Hide the toolbar with our new animation
        this.containerService.setToolbarVisible(false);

        // Step 2: Wait for toolbar hide animation to complete
        await new Promise((resolve) => setTimeout(resolve, 400));

        // Step 3: Update editingSlideSceneId and start slide transition
        this.editingSlideSceneId = slideSceneId;

        // Use the shared SlideerLib method for smooth transition
        const transitionPromise = TouchLib.handleSmoothSlideTransition(
            slideSceneId,
            (id) => (this.transitioningSlideId = id),
            () => this.cdr.detectChanges(),
        );

        // Trigger the actual edit change (slide will resize behind the mask)
        this.onLightEdit(slideSceneId);

        // Handle sidebar alignment if called from sidebar
        if (fromSidebar) {
            setTimeout(() => {
                this.alignSlideWithSidebarTop(index);
            }, 100);
        }

        // Step 4: Wait for the transition to complete
        await transitionPromise;

        // Step 5: Show the new slide's toolbar after transition completes
        this.containerService.setToolbarVisible(true);
    }

    private alignSlideWithSidebarTop(slideIndex: number): void {
        const slidesContainer = document.querySelector(".slides-container") as HTMLElement;
        const slideElements = document.querySelectorAll(".slides-container .slide-item");
        const targetSlide = slideElements[slideIndex] as HTMLElement;

        if (targetSlide && slidesContainer) {
            const viewportHeight = slidesContainer.clientHeight;
            const containerPadding = 20; // Account for container padding
            const targetPosition = Math.max(0, targetSlide.offsetTop - containerPadding);
            const scrollDistance = Math.abs(targetPosition - slidesContainer.scrollTop);

            // If the slide is more than 2 viewports away, jump instantly ('auto').
            // Otherwise, use the smooth animation.
            const scrollBehavior = scrollDistance > viewportHeight * 2 ? "auto" : "smooth";

            // Use scrollTo on the container instead of scrollIntoView to avoid affecting other UI elements
            slidesContainer.scrollTo({
                top: targetPosition,
                behavior: scrollBehavior,
            });
        }
    }

    async onMainSlideClick(index: number, slideSceneId: string): Promise<void> {
        // Switch back to slides view when clicking any main slide
        this.showSettingsPanel = false;
        // Main slide clicks should animate the slide to top (original behavior)
        this.selectedSlideIndex = index;
        this.activeThumbSceneId = slideSceneId;

        // Only proceed if this slide is not currently being edited
        if (this.editingSlideSceneId === slideSceneId) {
            return;
        }

        // Step 1: Hide the toolbar with our new animation
        this.containerService.setToolbarVisible(false);

        // Step 2: Wait for toolbar hide animation to complete
        await new Promise((resolve) => setTimeout(resolve, 400)); // Matches animation duration

        // Step 3: Update editingSlideSceneId and start slide transition
        this.editingSlideSceneId = slideSceneId;

        // Use the shared SlideerLib method for smooth transition
        const transitionPromise = TouchLib.handleSmoothSlideTransition(
            slideSceneId,
            (id) => (this.transitioningSlideId = id),
            () => this.cdr.detectChanges(),
        );

        // Trigger the actual edit change (slide will resize behind the mask)
        this.onLightEdit(slideSceneId);

        // Animate the slide to the top using container scrolling (cool effect!)
        const scrollPromise = new Promise<void>((resolve) => {
            setTimeout(() => {
                const slidesContainer = document.querySelector(".slides-container") as HTMLElement;
                const slideElements = document.querySelectorAll(".slides-container .slide-item");
                const targetSlide = slideElements[index] as HTMLElement;

                if (targetSlide && slidesContainer) {
                    const targetPosition = targetSlide.offsetTop;

                    // For main slide clicks, scroll to the exact slide position to bring it to top
                    slidesContainer.scrollTo({
                        top: targetPosition,
                        behavior: "smooth",
                    });

                    // Resolve after scroll animation completes
                    setTimeout(resolve, 800);
                } else {
                    resolve();
                }
            }, 100);
        });

        // Step 4: Wait for both transition and scroll to complete
        await Promise.all([transitionPromise, scrollPromise]);

        // Step 5: Show the new slide's toolbar after everything completes
        this.containerService.setToolbarVisible(true);
    }

    onMenuLightEdit(index: number, slideSceneId: string, event = null) {
        if (event) {
            event.stopPropagation();
        }
        // Menu edit should also animate to top like main slide clicks
        this.onMainSlideClick(index, slideSceneId);
    }

    private updateActiveSlideOnScroll(): void {
        const slidesContainer = document.querySelector(".slides-container") as HTMLElement;
        const sidebarContainer = document.querySelector(".sidebar-container") as HTMLElement;
        const slideElements = document.querySelectorAll(".slides-container .slide-item");

        if (!slidesContainer || !sidebarContainer || slideElements.length === 0) {
            return;
        }

        // Get viewport positions
        const containerRect = slidesContainer.getBoundingClientRect();
        const sidebarRect = sidebarContainer.getBoundingClientRect();
        const sidebarTopRelativeToContainer = sidebarRect.top - containerRect.top;

        let closestSlideIndex = 0;
        let minDistance = Infinity;

        // Find slide that's closest to the sidebar alignment position
        slideElements.forEach((slideElement, index) => {
            const slideRect = slideElement.getBoundingClientRect();
            const slideTopRelativeToContainer = slideRect.top - containerRect.top;

            // Calculate distance from slide top to sidebar alignment position
            const distance = Math.abs(slideTopRelativeToContainer - sidebarTopRelativeToContainer);

            if (distance < minDistance) {
                minDistance = distance;
                closestSlideIndex = index;
            }
        });

        // Update active slide if it changed (with tolerance to prevent flicker)
        if (closestSlideIndex !== this.selectedSlideIndex && minDistance < 80) {
            const slide = this.slideList[closestSlideIndex];
            if (slide) {
                this.selectedSlideIndex = closestSlideIndex;
                this.activeThumbSceneId = slide.sceneId;
                this.cdr.detectChanges();
            }
        }
    }

    toggleSlideMenu(event: Event, slideId: number) {
        event.stopPropagation();
        if (this.popupMenuId === slideId) {
            this.popupMenuId = null;
        } else {
            this.popupMenuId = slideId;
        }
    }

    onAdvanceEditSlide(slideSubSceneId: string, event = null) {
        if (event) {
            event.stopPropagation();
        }
        this.popupMenuId = null;
        if (!slideSubSceneId) return;
        this.updateSliderSelection(slideSubSceneId);

        this.storeSelector.dispatch(actionSceneJumpLinked({ previousTouchSceneId: this.sliderSceneId }));

        // // add this location change to prevent mouse drag issue on scene builder
        this.router.navigateByUrl("/editor/list", { skipLocationChange: true });
        setTimeout(() => {
            this.router.navigateByUrl(`editor/scene/${slideSubSceneId}`);
        });
    }

    onTouchButtonDrop(event: CdkDragDrop<any>) {
        console.log("🎯 onTouchButtonDrop called in touch-editor", {
            hasEvent: !!event,
            editingSlideSceneId: this.editingSlideSceneId,
        });

        if (!this.editingSlideSceneId) {
            this.toastr.warning("Please select a slide first");
            return;
        }

        // Calculate offset from the drop event
        const offset = this.sceneAddService.getOffset(event);

        console.log("✅ Touch button dropped on main canvas area - opening dialog", {
            offset,
            sliderScene: this.sliderScene,
        });

        // Open the touch action dialog (same as in touch-navigation component)
        this.dialog.open(DialogTouchActionComponent, {
            width: "500px",
            data: {
                sliderScene: this.sliderScene,
                newTouchButton: true,
                offset,
            },
        });
    }

    onDropSlide(event: CdkDragDrop<Slide[]>) {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        // Update the playlist pages order in the scene data first
        const currentPages = TouchLib.getCurrentPages(this.sliderScene);
        const playlistId = TouchLib.getPlaylistId(this.sliderScene);

        // Reorder pages array to match new slide order
        moveItemInArray(currentPages, event.previousIndex, event.currentIndex);

        // Update the local slide list with corrected IDs
        moveItemInArray(this.slideList, event.previousIndex, event.currentIndex);

        // Fix slide IDs to match their new positions
        this.slideList.forEach((slide, index) => {
            slide.id = index;
        });

        // Update selected index if needed
        if (this.selectedSlideIndex !== null) {
            if (this.selectedSlideIndex === event.previousIndex) {
                this.selectedSlideIndex = event.currentIndex;
            } else if (event.previousIndex < this.selectedSlideIndex && event.currentIndex >= this.selectedSlideIndex) {
                this.selectedSlideIndex--;
            } else if (event.previousIndex > this.selectedSlideIndex && event.currentIndex <= this.selectedSlideIndex) {
                this.selectedSlideIndex++;
            }
        }

        // Force change detection to update UI immediately
        this.cdr.detectChanges();

        // Dispatch update to store
        this.storeSelector.dispatch(
            actionUpdateSceneItem({
                sceneId: this.sliderSceneId,
                sceneItemId: playlistId,
                values: [
                    {
                        key: "Page",
                        cloneValue: true,
                        value: currentPages,
                        storePath: `Data[0].${this.mainComponent}[0]`,
                    },
                ],
            }),
        );

        // Update scene with proper error handling
        this.appService.updateMultiScene([this.sliderSceneId]);

        // Show success message
        this.toastr.success(this.translocoService.translate("toast.slidesReordered", {}, this.translocoService.getActiveLang()));
        setTimeout(() => {
            this.resetNavigation(true);
        }, 1000);
    }

    // Navigation
    onBackToList() {
        this.router.navigate(["editor/list"]);
    }

    showPreviewPopup() {
        // No need to check for selected slide - preview the entire slider

        // save current slide
        this.appService.updateMultiScene([this.editingSlideSceneId, this.sliderSceneId]);

        const dialogData: DialogPreviewData = {
            selectedSceneId: this.sliderSceneId,
            previewSceneId: this.sliderSceneId, // Preview the slider itself
            sceneData: this.sliderScene,
            editorWidth: parseInt(this.width),
            editorHeight: parseInt(this.height),
            isMainScene: true,
            sceneMimeShort: "Slider",
            coreAppData: this.coreAppData,
            isFullFeatures: this.isFullFeatures,
            isSlider: true,
            sliderName: this.sliderName,
        };

        const dialogRef = this.dialog.open(DialogPreviewComponent, {
            width: "600px",
            data: dialogData,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.action === "register") {
                this.showAddPlayerModal();
            }
        });
    }

    private showAddPlayerModal() {
        this.dialog.open(StationAddPlayerComponent, {
            height: "520px",
            width: "510px",
            data: {
                sceneId: this.sliderSceneId,
            },
        });
    }

    // Tools panel control
    toggleToolsPanel() {
        this.showToolsPanel = !this.showToolsPanel;
    }

    closeToolsPanel() {
        this.showToolsPanel = false;
    }

    // Sidebar panel control
    toggleSidebar() {
        this.sidebarMinimized = !this.sidebarMinimized;
        // Save state to localStorage
        this.localStoreService.setStorageValConfig("slideerSidebarMinimized", this.sidebarMinimized ? "1" : "0");
        this.cdr.detectChanges();
    }

    openSidebar() {
        this.sidebarMinimized = false;
        this.localStoreService.setStorageValConfig("slideerSidebarMinimized", "0");
        this.cdr.detectChanges();
    }

    closeSidebar() {
        this.sidebarMinimized = true;
        this.localStoreService.setStorageValConfig("slideerSidebarMinimized", "1");
        this.cdr.detectChanges();
    }

    onDeleteSlideFromMinimized() {
        // When called from minimized sidebar, use the currently selected slide scene ID
        // Add additional safety checks for newly created slides

        let sceneIdToDelete = null;

        // Try to get scene ID from multiple sources for reliability
        if (this.selectedSlideIndex !== null && this.selectedSlideIndex < this.slideList.length && this.slideList[this.selectedSlideIndex]) {
            sceneIdToDelete = this.slideList[this.selectedSlideIndex].sceneId;
        } else if (this.activeThumbSceneId) {
            // Verify activeThumbSceneId exists in current slideList
            const foundSlide = this.slideList.find((slide) => slide.sceneId === this.activeThumbSceneId);
            if (foundSlide) {
                sceneIdToDelete = this.activeThumbSceneId;
            }
        } else if (this.editingSlideSceneId) {
            // Use editingSlideSceneId as another fallback
            const foundSlide = this.slideList.find((slide) => slide.sceneId === this.editingSlideSceneId);
            if (foundSlide) {
                sceneIdToDelete = this.editingSlideSceneId;
            }
        }

        // Only proceed if we found a valid scene ID
        if (sceneIdToDelete) {
            this.onDeleteSlide(sceneIdToDelete);
        } else {
            console.warn("Could not determine which slide to delete from minimized sidebar");
        }
    }

    onLightEdit(slideSceneId = null) {
        this.popupMenuId = null;
        if (!slideSceneId && !this.activeThumbSceneId) return;
        if (slideSceneId) {
            this.updateSliderSelection(slideSceneId);
            this.storeSelector.dispatch(actionSelectedSceneId({ id: slideSceneId }));
        } else {
            this.updateSliderSelection(this.activeThumbSceneId);
            this.storeSelector.dispatch(actionSelectedSceneId({ id: this.activeThumbSceneId }));
        }
        if (!this.sidenav.opened) {
            this.sidenav.toggle(true);
        }
    }

    addTouchButtons() {}

    // Sidebar methods
    setViewMode(mode: "list" | "grid") {
        this.viewMode = mode;
    }

    onDuplicateSlide(slideSubSceneId: string, event = null) {
        if (event) {
            event.stopPropagation();
        }
        this.popupMenuId = null;
        if (!slideSubSceneId) return;

        this.cod(
            this.storeSelector
                .getScene(slideSubSceneId)
                .pipe(take(1))
                .subscribe((sceneData) => {
                    this.processDuplicate(sceneData);
                }),
        );
    }

    async processDuplicate(sceneData) {
        if (this.loading) return;
        this.loading = true;
        this.toastr.info(this.translocoService.translate("toast.duplicateSlide", {}, this.translocoService.getActiveLang()));

        const newSubScene = _.cloneDeep(sceneData);
        newSubScene.Player.$.label = `${this.sliderName} ${this.slideList.length + 1}`;
        const newSubSceneId = await this.importService.importPrebuiltScene(newSubScene, null, true);

        // add new scene page to playlist
        const currentPages = TouchLib.getCurrentPages(this.sliderScene);
        const playlistId = TouchLib.getPlaylistId(this.sliderScene);

        currentPages.push(TouchLib.createPlaylistPage(newSubScene.Player.$.label, newSubSceneId));

        this.storeSelector.dispatch(
            actionUpdateSceneItem({
                sceneId: this.sliderSceneId,
                sceneItemId: playlistId,
                values: [
                    {
                        key: "Page",
                        cloneValue: true,
                        value: currentPages,
                        storePath: `Data[0].${this.mainComponent}[0]`,
                    },
                ],
            }),
        );

        this.appService.updateMultiScene([this.sliderSceneId]);
        // Use single timeout to prevent race conditions
        setTimeout(() => {
            this.loading = false;
            this.resetNavigation(true);
        }, 700);
    }

    async addNewSlide() {
        if (this.loading) return;
        this.loading = true;

        this.toastr.info(this.translocoService.translate("toast.addSlide", {}, this.translocoService.getActiveLang()));
        const newSlideName = TouchLib.uniqueName("slide");

        // create sub-scene with a label
        const { scene: newSubScene, sceneId: subSceneId } = TouchLib.createNewSlideScene(this.width, this.height, this.storeSelector.blockLabels, newSlideName);
        const newSubSceneId = await this.importService.importPrebuiltScene(newSubScene, null, true);

        // add new scene page to playlist
        const currentPages = TouchLib.getCurrentPages(this.sliderScene);
        const playlistId = TouchLib.getPlaylistId(this.sliderScene);

        currentPages.push(TouchLib.createPlaylistPage(newSubScene.Player.$.label, newSubSceneId));

        this.storeSelector.dispatch(
            actionUpdateSceneItem({
                sceneId: this.sliderSceneId,
                sceneItemId: playlistId,
                values: [
                    {
                        key: "Page",
                        cloneValue: true,
                        value: currentPages,
                        storePath: `Data[0].${this.mainComponent}[0]`,
                    },
                ],
            }),
        );

        this.appService.updateMultiScene([this.sliderSceneId]);
        // Use single timeout to prevent race conditions
        setTimeout(() => {
            this.loading = false;
            this.resetNavigation(true);
        }, 700);
    }

    onDeleteSlide(slideSubSceneId: string = null, event = null) {
        if (event) {
            event.stopPropagation();
        }
        this.popupMenuId = null;

        // Prevent deletion during loading operations
        if (this.loading) {
            console.warn("Cannot delete slide while loading operation is in progress");
            return;
        }

        if (this.selectedSlideIndex === null && !slideSubSceneId) return;

        let selectedSlide;
        if (slideSubSceneId) {
            selectedSlide = this.slideList.find((slide) => slide.sceneId === slideSubSceneId);
            // Additional safety check: verify the scene exists in the store
            if (!selectedSlide) {
                console.warn("Could not find slide with scene ID:", slideSubSceneId, "in current slideList");
                return;
            }
        } else {
            selectedSlide = this.slideList[this.selectedSlideIndex];
        }

        if (!selectedSlide) return;

        const dialogRef = this.dialog.open(DialogPopComponent, {
            height: "200px",
            width: "400px",
            data: { name: `Delete "${selectedSlide.title}"?`, showCancel: true },
        });

        this.cod(
            dialogRef.afterClosed().subscribe((result) => {
                if (!result) return;
                this.removeSelectedSlide(selectedSlide);
            }),
        );
    }

    private removeSelectedSlide(selectedSlide: Slide) {
        if (!selectedSlide) return;

        let deletedSlideId = selectedSlide.sceneId;

        // Check if this is the last slide
        const isLastSlide = this.slideList.length === 1;

        // Get current pages array
        let currentPages = TouchLib.getCurrentPages(this.sliderScene);
        const playlistId = TouchLib.getPlaylistId(this.sliderScene);

        // Get next or first slide scene id
        if (this.editingSlideSceneId == selectedSlide.sceneId) {
            let nextSlideSceneId;
            if (!isLastSlide) {
                const ind = this.slideList.findIndex((s) => s.sceneId === deletedSlideId);
                if (ind < this.slideList.length - 1) {
                    nextSlideSceneId = this.slideList[ind + 1].sceneId;
                } else {
                    nextSlideSceneId = this.slideList[ind - 1].sceneId;
                }
                this.updateSliderSelection(nextSlideSceneId);
                this.storeSelector.dispatch(actionSelectedSceneId({ id: nextSlideSceneId }));
            } else {
                this.storeSelector.dispatch(actionSelectedSceneId({ id: null }));
            }
        }

        // Remove the slide from pages array
        currentPages = currentPages.filter((page, index) => page.Player[0].$.src !== deletedSlideId);

        // Update the store
        this.storeSelector.dispatch(
            actionUpdateSceneItem({
                sceneId: this.sliderSceneId,
                sceneItemId: playlistId,
                values: [
                    {
                        key: "Page",
                        cloneValue: true,
                        value: currentPages,
                        storePath: `Data[0].${this.mainComponent}[0]`,
                    },
                ],
            }),
        );

        // Remove the scene from the scenes store
        this.storeSelector.dispatch(
            actionRemoveScenes({
                sceneIds: [selectedSlide.sceneId],
            }),
        );

        // Delete the scene from database
        this.crudScene("delete", [selectedSlide.sceneId], null);

        // Update the scene
        setTimeout(() => {
            this.appService.updateMultiScene([this.sliderSceneId]);
        }, 500);

        // Show success message
        this.toastr.info(this.translocoService.translate("toast.removedSlider", {}, this.translocoService.getActiveLang()));

        // If this was the last slide, navigate back to slideer list
        if (isLastSlide) {
            setTimeout(() => {
                this.router.navigate(["editor/list"]);
            }, 1000); // Give time for the operations to complete
        } else {
            setTimeout(() => {
                this.resetNavigation(true);
            }, 1000);
        }
    }

    addBlock(block: ISliderBlock) {
        if (!block || !block.component) return;
        if (this.loading) return;
        this.loading = true;

        const blockId = block.type;

        switch (blockId) {
            case this.storeSelector.blockLabels.LABEL.id: {
                this.sceneAddService.addHeaderLabel("head");
                break;
            }
            case this.storeSelector.blockLabels.IMAGE.id:
                break;
            default:
                break;
        }
        this.loading = false;
    }

    dropBlock(event: CdkDragDrop<any[]>) {
        if (event.previousContainer === event.container) {
            // Do nothing if dropped in the same container
            return;
        }

        const tool = event.item.data;
        this.addBlock(tool);
    }

    private crudScene(command: ICRUDCommand, id, body) {
        const saveRecord: ICRUDRecord = {
            studioId: this.localStoreService.getStorageConfig().studioId,
            command,
            primary: "player_data_id",
            table: "player_data",
            body,
            id,
        };
        this.storeSelector.dispatch(actionCRUDRecord({ saveRecord }));
    }

    private updateSliderSelection(slideSubSceneId) {
        this.storeSelector.dispatch(actionSelectedSceneTouchId(TouchLib.createSelectedSliderPayload(String(this.sliderSceneId), this.sliderName, String(slideSubSceneId))));
    }

    override ngOnDestroy() {
        // Clear debounce timer to prevent memory leaks
        if (this.updateDebounceTimer) {
            clearTimeout(this.updateDebounceTimer);
        }
        // Clear scene data subscription to prevent memory leaks
        if (this.currentSceneDataSubscription) {
            this.currentSceneDataSubscription.unsubscribe();
        }
        this.storeSelector.dispatch(actionSelectedSceneId({ id: null }));
        this.storeSelector.dispatch(actionSelectedSceneItemId({ id: null }));
        super.ngOnDestroy();
    }

    onSliderSettings() {
        // Toggle settings panel view
        this.showSettingsPanel = true;

        // Deselect any selected slide in the left sidebar
        this.selectedSlideIndex = null;
        this.activeThumbSceneId = null;

        // Reset label visibility to visible
        this.baseLayerLabelVisible = "visible";

        // Update slider selection to announce the main wrapper scene as the selected slide
        this.updateSliderSelection(this.sliderSceneId);

        // Select the main wrapper scene (not the child slide)
        this.storeSelector.dispatch(actionSelectedSceneId({ id: this.sliderSceneId }));

        // Open the sidenav if not already opened
        if (!this.sidenav.opened) {
            this.sidenav.toggle(true);
        }

        this.cdr.detectChanges();

        // Switch to details template to show scene settings
        if (this.activeTemplate !== "details") {
            setTimeout(() => {
                this.setActiveTemplate("details");
                this.cdr.detectChanges();
            });
        }

        // Fade out the label after 2 seconds
        setTimeout(() => {
            this.baseLayerLabelVisible = "hidden";
            this.cdr.detectChanges();
        }, 2000);
    }

    onTouchAction() {
        this.dialog.open(DialogTouchActionComponent, {
            width: "500px",
            data: {
                sliderScene: this.sliderScene,
                newTouchButton: false,
                offset: null,
            },
        });
    }

    // Floating toolbar handlers
    onSearch() {
        this.toggleToolsPanel();
    }

    showTextTools() {
        this.toggleToolsPanel();
    }

    onImage() {
        this.toggleToolsPanel();
    }

    onGrid() {
        this.toggleToolsPanel();
    }

    onNewFeature() {
        this.toggleToolsPanel();
    }

    onSettings() {
        this.toggleToolsPanel();
    }

    onAnalytics() {
        this.toggleToolsPanel();
    }

    onLayers() {
        this.toggleToolsPanel();
    }

    resetNavigation(checkRemoveStatus = false, event = null) {
        this.removeNavigationButtons(false);
        setTimeout(() => {
            if (checkRemoveStatus && !this.isRemoved) {
                return;
            }
            let size: NavigationButtonSize = 2; // default navigation button size
            if (event && event.size) {
                size = event.size;
            }
            this.addNavigationButtons(size);
        }, 300);
    }

    /**
     * Generate label block with emoji icons for slide navigation
     */
    private getNavigationLabelTemplate(label: NavigationButtonType, size: NavigationButtonSize) {
        if (!label) return;

        // Calculate dimensions based on selected size (rectangular - wider than tall)
        const baseSizes = {
            1: { w: 60, h: 60, fontSize: 40 },
            2: { w: 75, h: 75, fontSize: 50 },
            3: { w: 90, h: 90, fontSize: 60 },
            4: { w: 105, h: 105, fontSize: 70 },
            5: { w: 120, h: 120, fontSize: 80 },
        };

        const { w, h, fontSize } = baseSizes[size] || baseSizes[2];

        // Create label block with emoji
        const labelBlockData: ISceneBlock = _.cloneDeep(this.storeSelector.blockLabels.LABEL.template);
        const newEventName = TouchLib.touchEventName(`${label}Page`);

        labelBlockData.$.id = _.uniqueId("__");
        labelBlockData.$.autoScaling = "0";
        labelBlockData.$.clickEventName = newEventName;
        labelBlockData.$.clickEventParam = "";
        labelBlockData.$.group = "collection_btn";

        const layout = labelBlockData.Data["0"].Layout["0"]?.$;

        layout.height = `${h}`;
        layout.bottom = `${0.5 * h}`;

        let emojiIcon: string;
        let command: string;

        // Get emoji arrows (using only arrow style)
        const emojiArrows = {
            first: "⏮️",
            prev: "◀️",
            next: "▶️",
            last: "⏭️",
        };

        switch (label) {
            case "prev": {
                layout.width = `${w}`;
                layout.left = `${2 * w}`;
                emojiIcon = emojiArrows[label];
                command = "prevPage";
                break;
            }
            case "first": {
                layout.width = `${w * 1.2}`;
                layout.left = `${0.5 * w}`;
                emojiIcon = emojiArrows[label];
                command = "firstPage";
                break;
            }
            case "next": {
                layout.width = `${w}`;
                layout.right = `${2 * w}`;
                emojiIcon = emojiArrows[label];
                command = "nextPage";
                break;
            }
            case "last": {
                layout.width = `${w * 1.2}`;
                layout.right = `${0.5 * w}`;
                emojiIcon = emojiArrows[label];
                command = "lastPage";
                break;
            }
        }

        const font = labelBlockData.Data["0"].Label["0"];
        font.Font["0"].$.fontSize = `${fontSize}`;
        font.Text = [emojiIcon];

        const template = TouchLib.getSendEventTemplate();

        template.$.from = newEventName;
        template.$.command = command;
        template.Params[0].Page[0].$.name = "Page";

        return {
            value: {
                key: "Player",
                cloneValue: true,
                value: labelBlockData,
                storePath: 'Data["0"].Scene["0"].Players[0]',
            },
            event: template,
        };
    }

    /**
     * Add First, Prev, Next, Last navigation buttons to the wrapper scene only
     * (not to individual child slides)
     */
    private addNavigationButtons(size: NavigationButtonSize) {
        if (this.slideList.length < 2) {
            return;
        }

        const collectionId = TouchLib.getPlaylistId(this.sliderScene);
        if (!collectionId) return;

        // Generate all 4 navigation buttons (first, prev, next, last)
        const commands: NavigationButtonType[] = ["first", "prev", "next", "last"];
        const labelBlocks = [];
        const eventBlocks = [];

        commands.forEach((_command) => {
            const { value, event } = this.getNavigationLabelTemplate(_command, size);
            labelBlocks.push(value);
            eventBlocks.push(event);
        });

        // Add navigation button blocks to the wrapper scene
        this.storeSelector.dispatch(
            actionAddSceneItem({
                sceneId: this.sliderSceneId,
                sceneItemId: this.sliderSceneId,
                values: labelBlocks,
                updatedItemIds: labelBlocks.map((_block) => _block.value.$.id),
                disableCRUD: false,
            }),
        );

        // Update EventCommands on the Collection/Playlist component
        const componentName = TouchLib.getComponentName(this.sliderScene);
        const players = this.sliderScene.Player.Data[0].Scene[0].Players[0].Player;
        const componentPlayer = players.find((p) => p.Data?.[0]?.[componentName]);
        if (!componentPlayer) return;
        const eventCommands = componentPlayer.Data[0].EventCommands;

        if (eventCommands && eventCommands[0]?.EventCommand) {
            // Add only new event commands that don't already exist
            const values = eventBlocks
                .filter((eventBlock) => {
                    return !eventCommands[0].EventCommand.find((_command) => _command.$.from === eventBlock.$.from);
                })
                .map((eventBlock) => ({
                    key: "EventCommand",
                    value: eventBlock,
                    storePath: `Data[0].EventCommands[0]`,
                }));

            if (values.length > 0) {
                this.storeSelector.dispatch(
                    actionAddSceneItem({
                        sceneId: this.sliderSceneId,
                        sceneItemId: collectionId,
                        values,
                        disableCRUD: true,
                    }),
                );
            }
        } else {
            // Create EventCommands structure if it doesn't exist
            if (eventBlocks.length > 0) {
                this.storeSelector.dispatch(
                    actionUpdateSceneItem({
                        sceneId: this.sliderSceneId,
                        sceneItemId: collectionId,
                        values: [
                            {
                                key: "EventCommands",
                                value: [
                                    {
                                        EventCommand: eventBlocks,
                                    },
                                ],
                                storePath: `Data[0]`,
                            },
                        ],
                        disableCRUD: true,
                    }),
                );
            }
        }

        setTimeout(() => {
            this.appService.updateMultiScene([this.sliderSceneId]);
        }, 500);
    }

    /**
     * Remove existing navigation buttons from the wrapper scene only
     */
    removeNavigationButtons(update = false) {
        // Get navigation buttons from the wrapper scene
        const players = this.sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
        if (!players || players.length === 0) return;

        const labelBlocks = players.filter((_player) => _player.$.group === "collection_btn");
        if (!labelBlocks || labelBlocks.length === 0) {
            this.isRemoved = false;
            return;
        }

        // Remove navigation button blocks from wrapper scene
        this.storeSelector.dispatch(
            actionRemoveSceneBlocks({
                sceneId: this.sliderSceneId,
                sceneItemId: null,
                values: labelBlocks.map((_block) => ({
                    key: null,
                    value: _block.$.id,
                    storePath: "Data[0].Scene[0].Players[0].Player",
                })),
                disableCRUD: false,
            }),
        );

        this.isRemoved = true;

        if (update) {
            setTimeout(() => {
                this.appService.updateMultiScene([this.sliderSceneId]);
            }, 500);
        }
    }
}
